import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { environment } from "@env";
import { User } from "@livechat/shared";
import { fromEvent } from "rxjs";
import { STORAGE_KEY as SESSION_STORAGE_KEY } from "@core/services/session.service";

type SpectatorData = {
  spectator: User,
  token?: string,
  spectatorToken?: string,
}

@Injectable({ providedIn: "root" })
export class SpectatorService {

  private readonly SPECTATOR_KEY = "spectator_mode";

  private spectatorData?: SpectatorData;
  private adminFrame?: HTMLIFrameElement;

  get isSpectating() {
    return !!this.spectatorData;
  }

  get spectator() {
    return this.spectatorData?.spectator;
  }

  get spectatorToken(): string | undefined {
    return this.spectatorData?.spectatorToken;
  }

  get isLegacyMode(): boolean {
    return !!(this.spectatorData?.token && this.spectatorData?.spectatorToken);
  }

  constructor(private http: HttpClient) {
    this.loadSpectatorData();

    if (this.isSpectating) {
      this.instantiateAdminFrame();
    }
  }

  public setupSpectatorListener() {
    fromEvent(window, "storage").subscribe((e: any) => {
      if (e.key !== this.SPECTATOR_KEY) {
        return;
      }

      if (!e.newValue) {
        this.clearUserSpecificStorage();
        if (this.isLegacyMode) {
          window.location.href = `${environment.app.url}/auth?token=${encodeURIComponent(this.spectatorData!.spectatorToken!)}`;
        } else {
          this.clearSessionAndReload();
        }
        return;
      }

      const payload = JSON.parse(e.newValue);
      if (payload.token) {
        window.location.href = `${environment.app.url}/auth?token=${encodeURIComponent(payload.token)}`;
      } else {
        this.clearSessionAndReload();
      }
    });
  }

  private instantiateAdminFrame() {
    const iframe = document.createElement("iframe");
    iframe.hidden = true;
    iframe.src = `${environment.externalUrls.platform}/assets/static/spectator.html`;

    document.body.appendChild(iframe);
    this.adminFrame = iframe;
  }

  public async endSpectating() {
    if (!this.isSpectating) return;

    const isLegacy = this.isLegacyMode;

    try {
      await this.http.post(
        `${environment.externalUrls.bff}/auth/spectate/stop`,
        {},
        { withCredentials: true }
      ).toPromise();
    } catch (e) {
      console.error('[SpectatorService] Failed to call spectate/stop:', e);
    }

    localStorage.removeItem(this.SPECTATOR_KEY);

    if (this.adminFrame) {
      this.adminFrame.contentWindow?.postMessage("asksuite_spectator_end", "*");
    }

    this.clearUserSpecificStorage();

    if (isLegacy && this.spectatorData?.spectatorToken) {
      window.location.href = `${environment.app.url}/auth?token=${encodeURIComponent(this.spectatorData.spectatorToken)}`;
    } else {
      this.clearSessionAndReload();
    }
  }

  private clearUserSpecificStorage(): void {
    const staleKeys = [
      'kanban_mode_open',
      'kanban-active-pipeline-id',
      'kanban-lane-sort-preference',
      'kanban-lane-tag-preference',
      'phone_status_sync',
      'language',
      'notification_news',
      'kanban-feedback-creation',
      'kanban-feedback-usage',
      'attendance-filters_undefined',
    ];
    staleKeys.forEach((key) => localStorage.removeItem(key));

    const spectatedUserId = this.spectatorData?.spectator?.id;
    if (spectatedUserId) {
      localStorage.removeItem(`attendance-filters_${spectatedUserId}`);
    }
  }

  private clearSessionAndReload() {
    localStorage.removeItem(SESSION_STORAGE_KEY);
    window.location.reload();
  }

  private loadSpectatorData() {
    const storedSpectator = localStorage.getItem(this.SPECTATOR_KEY);

    if (!storedSpectator) return;

    try {
      const parsed = JSON.parse(storedSpectator);

      this.spectatorData = parsed.spectator ? parsed : { spectator: parsed };
    }
    catch (e) {
      console.log("Invalid spectator data stored");
    }
  }
}
